
<template>
  <div ref="mermaidContainer" class="mermaid-diagram"></div>
</template>

<script>
import mermaid from 'mermaid'

export default {
  props: {
    message: {
      type: Object,
      required: true
    }
  },
  computed: {
    diagramData() {
      return this.message.display.meta
    }
  },
  data() {
    return {}
  },
  mounted() {
    // Initialize mermaid with startOnLoad: false to prevent auto-rendering
    mermaid.initialize({ startOnLoad: false })
    this.$nextTick(() => {
      this.renderDiagram()
    })
  },
  watch: {
    diagramData: {
      handler() {
        if (this.$refs.mermaidContainer) {
          this.renderDiagram()
        }
      },
      immediate: false
    }
  },
  methods: {
    /**
     * Rasterize the rendered SVG to a PNG data URL for download.
     * @returns {Promise<string|null>}
     */
    exportChartImage() {
      const container = this.$refs.mermaidContainer
      if (!container) {
        return Promise.resolve(null)
      }
      const svg = container.querySelector("svg")
      if (!svg) {
        return Promise.resolve(null)
      }
      return new Promise(function (resolve) {
        let source = new XMLSerializer().serializeToString(svg)
        if (!source.match(/^<svg[^>]+xmlns=/)) {
          source = source.replace("<svg", '<svg xmlns="http://www.w3.org/2000/svg"')
        }
        const rect = svg.getBoundingClientRect()
        const w = Math.max(1, Math.ceil(rect.width) || 800)
        const h = Math.max(1, Math.ceil(rect.height) || 600)
        const svgBlob = new Blob([source], { type: "image/svg+xml;charset=utf-8" })
        const url = URL.createObjectURL(svgBlob)
        const img = new Image()
        img.onload = function () {
          const canvas = document.createElement("canvas")
          canvas.width = w
          canvas.height = h
          const ctx = canvas.getContext("2d")
          ctx.fillStyle = "#ffffff"
          ctx.fillRect(0, 0, w, h)
          ctx.drawImage(img, 0, 0)
          URL.revokeObjectURL(url)
          try {
            resolve(canvas.toDataURL("image/png"))
          } catch (e) {
            resolve(null)
          }
        }
        img.onerror = function () {
          URL.revokeObjectURL(url)
          resolve(null)
        }
        img.src = url
      })
    },
    renderDiagram() {
      if (!this.diagramData || !this.$refs.mermaidContainer) {
        return
      }

      // Generate unique ID for this diagram
      const diagramId = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

      try {
        // Clear previous content
        this.$refs.mermaidContainer.innerHTML = ''
        
        // Create a div with the mermaid class and diagram code
        const mermaidDiv = document.createElement('div')
        mermaidDiv.id = diagramId
        mermaidDiv.className = 'mermaid'
        mermaidDiv.textContent = this.diagramData
        this.$refs.mermaidContainer.appendChild(mermaidDiv)

        // Render using mermaid v8 API
        mermaid.init(undefined, mermaidDiv)
      } catch (error) {
        console.error('Error rendering mermaid diagram:', error)
        this.$refs.mermaidContainer.innerHTML = `<div class="mermaid-error">Error rendering diagram: ${error.message}</div>`
      }
    }
  }
}
</script>

<style scoped>
.mermaid-diagram {
  width: 100%;
  overflow-x: auto;
}

.mermaid-error {
  color: red;
  padding: 10px;
  border: 1px solid red;
  border-radius: 4px;
}
</style>